import { css } from '@emotion/react';
import {
  AspectRatio,
  Badge,
  Box,
  Divider,
  Group,
  Icon,
  OptimizeImage,
  Text,
  desktopFirstMediaQuery,
  useTheme,
} from '@inflearn/ds-react';
import { faHeart } from '@inflearn/pro-regular-svg-icons';
import { faHashtag, faLockKeyhole } from '@inflearn/pro-solid-svg-icons';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { Fragment } from 'react';

import type { BlogUserProfileResponse } from '../../../../../resources/@types/uccSchema';
import { INFLEARN_URL } from '../../../../constants/urls';
import { checkIsPublicBlog } from '../../../../utils/blog/blogType';
import { formatDate } from '../../../../utils/date';
import { filterHTMLTag } from '../../../../utils/filterHTMLTag';
import { formatCompactNumber } from '../../../../utils/format/number';

type Props = Omit<BlogUserProfileResponse, 'writer'>;

export default function BlogItem({ tags = [], ...blog }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();

  const tagTotalCount = tags?.length || 0;
  const hasCategoryTag = tagTotalCount !== 0 || Boolean(blog?.category);

  return (
    <Link href={INFLEARN_URL.BLOG_DETAIL(blog.id)} aria-label={t('블로그 상세페이지로 이동')}>
      <Box
        pos="relative"
        css={{
          [desktopFirstMediaQuery.mobile]: {
            width: '100%',
            maxWidth: '100%',
          },
        }}
      >
        <Box
          css={{
            [desktopFirstMediaQuery.mobile]: {
              display: 'flex',
              flexDirection: 'row-reverse',
              gap: 16,
            },
          }}
        >
          {checkIsPublicBlog(blog.status) ? null : (
            <Badge
              pos="absolute"
              top={12}
              left={12}
              variant="filled"
              radius="sm"
              color="gray"
              size="lg"
              leftSection={<Icon icon={faLockKeyhole} size="xs" />}
              css={{
                zIndex: 1,
                [desktopFirstMediaQuery.mobile]: {
                  ...stylePrivateBadge,
                },
              }}
            >
              {t('비공개')}
            </Badge>
          )}
          <AspectRatio
            bg="gray.2"
            ratio={3 / 1.57}
            css={{
              borderRadius: 8,
              overflow: 'hidden',
              border: `1px solid ${theme.colors.gray[2]}`,
              img: {
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              },

              [desktopFirstMediaQuery.mobile]: {
                flex: '64px 0 0',
                height: 64,
                borderRadius: 4,
              },
            }}
          >
            <OptimizeImage w={260} alt={blog.title} src={blog.coverUrl} />
          </AspectRatio>
          <Box
            px={4}
            mt={16}
            css={{
              [desktopFirstMediaQuery.mobile]: {
                flex: 1,
                padding: 0,
                marginTop: 0,
              },
            }}
          >
            <Group pb={4} spacing={6} align="center">
              <Text color="gray.7" size="xs" weight={500}>
                {formatDate(blog.createdAt, 'L')}
              </Text>
              <Divider variant="solid" orientation="vertical" h={8} mt={5} />
              <Text color="gray.7" size="xs" weight={500}>
                <Icon
                  icon={faHeart}
                  size="xs"
                  color="gray.7"
                  css={{ marginRight: 4, verticalAlign: 'middle' }}
                />

                {formatCompactNumber(blog.likeCount)}
              </Text>
            </Group>
            <Text
              size="md"
              weight={500}
              color="gray.9"
              lineClamp={2}
              css={{ wordBreak: 'break-all' }}
            >
              {blog.title}
            </Text>
            <Text size="sm" color="gray.5" lineClamp={2} mt={4} css={{ wordBreak: 'break-all' }}>
              {filterHTMLTag(blog.body)}
            </Text>
          </Box>
        </Box>
        {hasCategoryTag ? (
          <Group
            noWrap
            pl={4}
            mt={12}
            align="center"
            css={{
              overflow: 'hidden',
              [desktopFirstMediaQuery.mobile]: { paddingLeft: 0 },
            }}
          >
            <Icon icon={faHashtag} size="2xs" color="gray.6" css={{ marginRight: 6 }} />
            {[blog?.category, ...tags].filter(Boolean).map((item, index) => (
              <Fragment key={item?.id}>
                {index !== 0 ? (
                  <Text color="gray.3" size="xs" weight={500} mx={2}>
                    ･
                  </Text>
                ) : null}
                <Text
                  key={item?.id}
                  size="xs"
                  weight={500}
                  color="gray.6"
                  css={{ whiteSpace: 'nowrap' }}
                >
                  {item?.name}
                </Text>
              </Fragment>
            ))}
            <Box css={styleGradient} />
          </Group>
        ) : null}
      </Box>
    </Link>
  );
}

const stylePrivateBadge = css({
  width: '64px',
  height: '64px',
  flexDirection: 'column',
  justifyContent: 'center',
  right: '0',
  top: '0',
  left: 'auto',
  backgroundColor: 'rgba(73, 80, 87, 0.9)',

  span: {
    marginRight: 0,
    marginBottom: 4,
  },
});

const styleGradient = css({
  width: 48,
  height: 18,
  position: 'absolute',
  right: 0,
  bottom: 0,
  background: 'linear-gradient(270deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)',
});
